<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import { registry, categories } from '@/calculators/registry'
import type { AnyCalculatorDefinition } from '@/types/calculator'
import Icon from '@/components/icons/Icon.vue'

const props = defineProps<{
  related: string[]
}>()

const relatedCalculators = computed(() =>
  props.related
    .map((slug) => registry.find((c) => c.slug === slug))
    .filter((c): c is AnyCalculatorDefinition => Boolean(c))
)

const categoryMap = Object.fromEntries(categories.map((c) => [c.id, c]))
</script>

<template>
  <section v-if="relatedCalculators.length > 0" class="related" aria-labelledby="related-title">
    <h2 id="related-title" class="related__title">Related calculators</h2>
    <ul class="related__list">
      <li v-for="calc in relatedCalculators" :key="calc.id" class="related__item">
        <RouterLink :to="`/${calc.slug}`" class="related__link">
          <span class="related__icon">
            <Icon :name="categoryMap[calc.category]?.icon ?? 'calculator'" :size="18" />
          </span>
          <span class="related__content">
            <span class="related__name">{{ calc.shortTitle || calc.title }}</span>
            <span class="related__desc">{{ calc.description }}</span>
          </span>
          <Icon name="arrow-right" :size="16" class="related__arrow" />
        </RouterLink>
      </li>
    </ul>
  </section>
</template>

<style scoped>
.related {
  margin-top: var(--space-10);
}

.related__title {
  font-size: var(--text-xl);
  font-weight: var(--font-semibold);
  margin-bottom: var(--space-4);
}

.related__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: var(--space-3);
}

.related__link {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-4);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background-color: var(--color-surface);
  text-decoration: none;
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease,
    transform 0.05s ease;
}

.related__link:hover {
  border-color: var(--color-primary-light);
  background-color: var(--color-surface-raised);
  transform: translateY(-1px);
}

.related__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: var(--radius-md);
  background: linear-gradient(135deg, rgb(10 92 86 / 0.1), rgb(13 148 136 / 0.06));
  color: var(--color-primary);
}

.related__content {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: var(--space-1);
  min-width: 0;
}

.related__name {
  font-weight: var(--font-semibold);
  color: var(--color-primary);
}

.related__desc {
  font-size: var(--text-sm);
  color: var(--color-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Compact rows on phones: hide the description, shrink the icon and
   padding so each related link is a single-line tap target. */
@media (max-width: 639px) {
  .related {
    margin-top: var(--space-8);
  }

  .related__link {
    padding: var(--space-3);
  }

  .related__icon {
    width: 32px;
    height: 32px;
  }

  .related__desc {
    display: none;
  }
}

.related__arrow {
  flex-shrink: 0;
  color: var(--color-text-muted);
  transition: transform 0.15s ease;
}

.related__link:hover .related__arrow {
  transform: translateX(3px);
  color: var(--color-primary);
}
</style>
